import Phaser from "phaser";
import { MAP_SIZE, TILE, worldToScreen, type ScreenPos } from "../map/Iso";

const ELEV_PX = 6;
const FLAME_COUNT = 6;

/**
 * An animated fire placed on the iso map.
 * Flames are simple circles that flicker via tweens.
 */
export class Fire {
  readonly worldX: number;
  readonly worldY: number;
  private container: Phaser.GameObjects.Container;
  private flames: Phaser.GameObjects.Arc[] = [];
  private tweens: Phaser.Tweens.Tween[] = [];
  private alive = true;

  constructor(scene: Phaser.Scene, origin: ScreenPos, tx: number, ty: number, elevation: number) {
    this.worldX = tx;
    this.worldY = ty;

    const elevPx = elevation * ELEV_PX;
    const { sx, sy } = worldToScreen(tx, ty);
    const screenX = origin.sx + sx + TILE.halfW;
    const screenY = origin.sy + sy + TILE.height - 4 - elevPx;

    this.container = scene.add.container(screenX, screenY);
    this.container.setDepth((tx + ty) * 1000 + 2);

    // Build flame particles as overlapping circles
    const colors = [0xff4400, 0xff6600, 0xffaa00, 0xffcc33, 0xff3300, 0xffdd44];
    for (let i = 0; i < FLAME_COUNT; i++) {
      const radius = 3 + Math.random() * 3;
      const offsetX = (Math.random() - 0.5) * 10;
      const baseY = -4 - Math.random() * 8;
      const flame = scene.add.circle(offsetX, baseY, radius, colors[i % colors.length]!, 0.85);
      this.container.add(flame);
      this.flames.push(flame);

      // Flickering tween — each flame moves up/down with random timing
      const tw = scene.tweens.add({
        targets: flame,
        y: baseY - 4 - Math.random() * 6,
        alpha: { from: 0.6 + Math.random() * 0.3, to: 0.2 + Math.random() * 0.3 },
        scaleX: { from: 0.8, to: 1.2 },
        scaleY: { from: 1, to: 1.4 },
        duration: 200 + Math.random() * 300,
        yoyo: true,
        repeat: -1,
        delay: Math.random() * 200,
      });
      this.tweens.push(tw);
    }

    // Glow circle underneath
    const glow = scene.add.circle(0, 0, 12, 0xff6600, 0.2);
    this.container.add(glow);
    scene.tweens.add({
      targets: glow,
      alpha: { from: 0.15, to: 0.3 },
      scaleX: { from: 0.9, to: 1.1 },
      scaleY: { from: 0.9, to: 1.1 },
      duration: 400,
      yoyo: true,
      repeat: -1,
    });
  }

  get isAlive(): boolean {
    return this.alive;
  }

  /** Screen position of the fire (for prompt placement). */
  get screenX(): number {
    return this.container.x;
  }

  get screenY(): number {
    return this.container.y;
  }

  /** Distance (in world tiles) from a point to this fire's tile center. */
  distanceTo(wx: number, wy: number): number {
    const dx = this.worldX + 0.5 - wx;
    const dy = this.worldY + 0.5 - wy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /** Play extinguish effect and destroy. */
  extinguish(scene: Phaser.Scene, onComplete?: () => void): void {
    if (!this.alive) return;
    this.alive = false;

    // Stop flame tweens
    for (const tw of this.tweens) tw.stop();

    // Smoke puffs
    for (let i = 0; i < 4; i++) {
      const smoke = scene.add.circle(
        this.container.x + (Math.random() - 0.5) * 8,
        this.container.y - 6,
        4 + Math.random() * 3,
        0x888888,
        0.6,
      );
      smoke.setDepth((this.worldX + this.worldY) * 1000 + 3);
      scene.tweens.add({
        targets: smoke,
        y: smoke.y - 16 - Math.random() * 10,
        alpha: 0,
        scaleX: 2,
        scaleY: 2,
        duration: 500 + Math.random() * 300,
        delay: i * 80,
        ease: "Quad.easeOut",
        onComplete: () => smoke.destroy(),
      });
    }

    // Fade out flames
    scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 400,
      ease: "Sine.easeOut",
      onComplete: () => {
        this.container.destroy();
        onComplete?.();
      },
    });
  }

  destroy(): void {
    for (const tw of this.tweens) tw.stop();
    this.container.destroy();
    this.alive = false;
  }
}

/**
 * Pick a random walkable tile that isn't occupied by a tree or the given excluded positions.
 */
export function randomFirePosition(
  excludeTiles: Array<{ worldX: number; worldY: number }>,
): { tx: number; ty: number } {
  const margin = 2;
  for (let attempt = 0; attempt < 50; attempt++) {
    const tx = margin + Math.floor(Math.random() * (MAP_SIZE - margin * 2));
    const ty = margin + Math.floor(Math.random() * (MAP_SIZE - margin * 2));
    const occupied = excludeTiles.some((t) => t.worldX === tx && t.worldY === ty);
    if (!occupied) return { tx, ty };
  }
  // Fallback
  return { tx: 5, ty: 5 };
}
